import { useId, useMemo, useState } from "react";
import * as Label from "@radix-ui/react-label";
import { useNavigate } from "react-router-dom";
import { format, isSameMonth, startOfMonth } from "date-fns";
import { es } from "date-fns/locale";
import {
  AvailabilityCalendar,
  AvailabilityCalendarSkeleton,
  Button,
  CmsPageHeader,
  CmsPageError,
  RadioButton,
  Select,
  Skeleton,
} from "@/components";
import type {
  CalendarFocusedTarget,
  CalendarSelectionRange,
} from "@/components";
import {
  ConfirmDialog,
  RecurrenceScopeDialog,
  Table,
  TableSkeleton,
} from "@/components/molecules";
import type { TableColumn } from "@/components/molecules";
import {
  EVENT_BOOKING_STATUS_LABELS,
  EVENT_CATEGORY_LABELS,
  EVENT_VISIBILITY_LABELS,
  MANAGER_ROUTES,
  MONTH_SELECT_LABELS,
} from "@/constants";
import {
  useAvailabilityCalendar,
  useCalendarEventMutations,
  useCmsSectionPath,
  useBackofficeTenantScope,
  useTenant,
} from "@/hooks";
import { BackofficeTopBarContainer } from "@/layouts/backoffice";
import { ApiError } from "@/services";
import type {
  AvailabilityCalendarEvent,
  CalendarEventCreateDraft,
  CalendarEventManage,
  EventBookingStatus,
  RecurrenceScope,
} from "@/types";
import {
  buildManagerBookingContactWhatsAppMessage,
  generateWhatsAppLink,
} from "@/utils";
import {
  buildMonthDate,
  buildYearSelectOptions,
  filterEventsOverlappingMonth,
} from "@/utils/calendarMonthRange";
import "../backoffice/CmsShared.scss";

const LIST_MONTH_CURRENT_OPTION = { current: "Mes actual" } as const;

const CALENDAR_PAGE_VIEW_OPTIONS = {
  calendar: "Vista calendario",
  list: "Vista lista",
} as const;

type ListStatusFilter = "all" | EventBookingStatus;

const LIST_STATUS_FILTER_OPTIONS: Record<ListStatusFilter, string> = {
  all: "Todos los eventos",
  PENDING_CONTACT: EVENT_BOOKING_STATUS_LABELS.PENDING_CONTACT,
  CONFIRMED: EVENT_BOOKING_STATUS_LABELS.CONFIRMED,
  REJECTED: EVENT_BOOKING_STATUS_LABELS.REJECTED,
};

const buildColumns = (): readonly TableColumn<CalendarEventManage>[] => [
  {
    id: "title",
    header: "Título",
    cell: (row) => row.title,
    className: "table__cell--truncate",
  },
  {
    id: "bookingStatus",
    header: "Estado del evento",
    cell: (row) => (
      <span
        className={`cms-booking-badge ${row.bookingStatus === "PENDING_CONTACT" && "cms-booking-badge--pending"} ${row.bookingStatus === "REJECTED" && "cms-booking-badge--rejected"}`}
      >
        {EVENT_BOOKING_STATUS_LABELS[row.bookingStatus]}
      </span>
    ),
  },
  {
    id: "when",
    header: "Horario",
    cell: (row) =>
      `${format(new Date(row.startsAt), "d/M HH:mm", { locale: es })} – ${format(new Date(row.endsAt), "HH:mm", { locale: es })}`,
  },
  {
    id: "visibility",
    header: "Visibilidad",
    cell: (row) => EVENT_VISIBILITY_LABELS[row.visibility],
  },
  {
    id: "category",
    header: "Categoría",
    cell: (row) => (row.category ? EVENT_CATEGORY_LABELS[row.category] : "—"),
  },
  {
    id: "contact",
    header: "Contacto",
    cell: (row) => row.contact?.name ?? "—",
  },
];

type PendingCalendarAction =
  | {
      type: "create";
      range: CalendarSelectionRange;
      hasConflict: boolean;
    }
  | {
      type: "edit";
      eventId: string;
      title: string;
    };

/** Manager calendar overview with week grid and events table */
export function CalendarEventsCms() {
  const roomFilterId = useId();
  const navigate = useNavigate();
  const scopeSlug = useBackofficeTenantScope();
  const { effectiveTenantSlug, space } = useTenant();
  const tenantSlug = scopeSlug ?? effectiveTenantSlug;
  const { remove } = useCalendarEventMutations(tenantSlug);
  const eventsPath = useCmsSectionPath(
    `${MANAGER_ROUTES.calendar}/${MANAGER_ROUTES.events}`,
  );
  const {
    rooms,
    selectedRoomId,
    setSelectedRoomId,
    weekStart,
    setWeekStart,
    events: calendarEvents,
    manageEvents,
    loading,
    error,
    reload,
  } = useAvailabilityCalendar({
    mode: "manage",
    tenantSlug,
  });
  const [listError, setListError] = useState<string | null>(null);
  const [isListLoading, setIsListLoading] = useState(false);
  const [selectionRange, setSelectionRange] =
    useState<CalendarSelectionRange | null>(null);
  const [focusedTarget, setFocusedTarget] =
    useState<CalendarFocusedTarget>(null);
  const [pendingAction, setPendingAction] =
    useState<PendingCalendarAction | null>(null);
  const [selectedView, setSelectedView] = useState<"calendar" | "list">(
    "calendar",
  );
  const [listStatusFilter, setListStatusFilter] =
    useState<ListStatusFilter>("all");
  const columns = useMemo(() => buildColumns(), []);
  const [listMonth, setListMonth] = useState(() => startOfMonth(new Date()));
  const yearOptions = useMemo(() => buildYearSelectOptions(), []);
  const listYear = format(listMonth, "yyyy");
  const listMonthIndex = String(listMonth.getMonth() + 1);
  const isListMonthCurrent = isSameMonth(listMonth, new Date());
  const listEvents = useMemo(() => {
    const monthEvents = filterEventsOverlappingMonth(manageEvents, listMonth);

    if (listStatusFilter === "all") {
      return monthEvents;
    }

    return monthEvents.filter(
      (event) => event.bookingStatus === listStatusFilter,
    );
  }, [manageEvents, listMonth, listStatusFilter]);

  const applyListMonth = (date: Date) => {
    const nextMonth = startOfMonth(date);
    setListMonth(nextMonth);
    setWeekStart(startOfMonth(nextMonth));
  };

  const handleListMonthPartChange = (year: string, month: string) => {
    applyListMonth(buildMonthDate(Number(year), Number(month)));
  };

  const handleListMonthCurrent = () => {
    applyListMonth(new Date());
  };

  const confirmCopy = useMemo(() => {
    if (!pendingAction) {
      return null;
    }

    if (pendingAction.type === "create") {
      return pendingAction.hasConflict
        ? {
            title: "Crear evento superpuesto",
            description: "¿Crear un evento con horarios superpuestos?",
            confirmLabel: "Crear",
          }
        : {
            title: "Crear evento",
            description: "¿Querés crear un evento en este horario?",
            confirmLabel: "Crear",
          };
    }

    return {
      title: "Editar evento",
      description: `¿Querés editar el evento "${pendingAction.title}"?`,
      confirmLabel: "Editar",
    };
  }, [pendingAction]);

  const [deleteTarget, setDeleteTarget] = useState<CalendarEventManage | null>(
    null,
  );
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);

  const executeDelete = async (eventId: string, scope?: RecurrenceScope) => {
    setListError(null);
    setIsListLoading(true);

    try {
      await remove.mutateAsync({ id: eventId, scope });
      await reload();
    } catch (err) {
      setListError(
        err instanceof ApiError
          ? (err.responseData?.message ?? err.message)
          : "Error al eliminar el evento.",
      );
    } finally {
      setIsListLoading(false);
    }
  };

  const handleDelete = (event: CalendarEventManage) => {
    if (event.seriesId) {
      setDeleteTarget(event);
      setScopeDialogOpen(true);
    } else {
      void executeDelete(event.id);
    }
  };

  const handleScopeDelete = (scope: RecurrenceScope) => {
    if (deleteTarget) {
      void executeDelete(deleteTarget.id, scope);
      setDeleteTarget(null);
    }
  };

  const handleSelectionActivate = ({
    range,
    hasConflict,
  }: {
    range: CalendarSelectionRange;
    hasConflict: boolean;
  }) => {
    setPendingAction({ type: "create", range, hasConflict });
  };

  const handleEventActivate = (event: AvailabilityCalendarEvent) => {
    const eventId = event.sourceEventId ?? event.id;
    const title = event.title?.trim() || "Ocupado";
    setPendingAction({ type: "edit", eventId, title });
  };

  const handleConfirmAction = () => {
    if (!pendingAction) {
      return;
    }

    if (pendingAction.type === "edit") {
      navigate(`${eventsPath}/${pendingAction.eventId}/editar`);
      setPendingAction(null);
      setFocusedTarget(null);
      return;
    }

    const { range, hasConflict } = pendingAction;
    const draft: CalendarEventCreateDraft = {
      startsAt: range.start.toISOString(),
      endsAt: range.end.toISOString(),
      ...(hasConflict
        ? {
            visibility: "INTERNAL" as const,
            blockRooms: false,
            roomIds: [],
          }
        : selectedRoomId
          ? { roomIds: [selectedRoomId] }
          : {}),
    };

    navigate(`${eventsPath}/${MANAGER_ROUTES.new}`, { state: { draft } });
    setPendingAction(null);
    setSelectionRange(null);
    setFocusedTarget(null);
  };

  if (loading) {
    return (
      <BackofficeTopBarContainer title="Panel de gestión">
        <div className="cms-page">
          <CmsPageHeader
            title="Calendario"
            center={
              <>
                <div className="cms-page-header__view-toggle cms-page-header__view-toggle--desktop">
                  <Skeleton
                    width={130}
                    height={28}
                    borderRadius="base"
                    borderWeight="base"
                  />
                  <Skeleton
                    width={90}
                    height={28}
                    borderRadius="base"
                    borderWeight="base"
                  />
                </div>
                <div className="cms-page-header__view-toggle cms-page-header__view-toggle--mobile">
                  <Skeleton
                    width="100%"
                    height={28}
                    borderRadius="base"
                    borderWeight="base"
                  />
                </div>
              </>
            }
            action={
              <Skeleton
                width={138}
                height={39}
                borderRadius="base"
                borderWeight="base"
              />
            }
          />
          <AvailabilityCalendarSkeleton />
        </div>
      </BackofficeTopBarContainer>
    );
  }

  return (
    <BackofficeTopBarContainer title="Panel de gestión">
      <div className="cms-page">
        <CmsPageHeader
          title="Calendario"
          center={
            <>
              <div className="cms-page-header__view-toggle cms-page-header__view-toggle--desktop">
                <RadioButton
                  options={CALENDAR_PAGE_VIEW_OPTIONS}
                  value={selectedView}
                  onChange={(value) =>
                    setSelectedView(value as "calendar" | "list")
                  }
                  variant="secondary"
                  density="compact"
                />
              </div>
              <div className="cms-page-header__view-toggle cms-page-header__view-toggle--mobile">
                <Select
                  options={CALENDAR_PAGE_VIEW_OPTIONS}
                  value={selectedView}
                  onChange={(value) =>
                    setSelectedView(value as "calendar" | "list")
                  }
                  density="compact"
                />
              </div>
            </>
          }
          action={
            <Button
              variant="primary"
              actionTrackSufix="bo_calendar_event_add"
              onClick={() => navigate(`${eventsPath}/${MANAGER_ROUTES.new}`)}
              motionScale="base"
            >
              Crear evento
            </Button>
          }
        />
        <CmsPageError error={error?.message ?? listError} />
        {rooms.length > 1 && selectedRoomId && (
          <div className="cms-field">
            <Label.Root htmlFor={roomFilterId} className="cms-field__label">
              Sala
            </Label.Root>
            <Select
              id={roomFilterId}
              options={Object.fromEntries(
                rooms.map((room) => [room.id, room.name]),
              )}
              value={selectedRoomId}
              onChange={setSelectedRoomId}
              density="compact"
            />
          </div>
        )}
        {selectedView === "calendar" && (
          <AvailabilityCalendar
            placeholderEventTitle="Doble tap para crear este evento"
            events={calendarEvents}
            focusedTarget={focusedTarget}
            manageInteractions
            onEventActivate={handleEventActivate}
            onFocusedTargetChange={setFocusedTarget}
            onSelectionActivate={handleSelectionActivate}
            onSelectionRangeChange={setSelectionRange}
            onWeekChange={setWeekStart}
            selectionRange={selectionRange}
            weekStart={weekStart}
          />
        )}
        {selectedView === "list" && (
          <>
            <div className="cms-page__events-list-toolbar">
              <div className="cms-page__events-list-toolbar-slot--start">
                <RadioButton
                  options={LIST_MONTH_CURRENT_OPTION}
                  value={isListMonthCurrent ? "current" : ""}
                  onChange={(mode) => {
                    if (mode === "current") {
                      handleListMonthCurrent();
                    }
                  }}
                  variant="secondary"
                  density="compact"
                />
                <Select
                  options={LIST_STATUS_FILTER_OPTIONS}
                  value={listStatusFilter}
                  onChange={(value) =>
                    setListStatusFilter(value as ListStatusFilter)
                  }
                  density="compact"
                  fullWidth={false}
                />
              </div>
              <div className="cms-page__events-list-toolbar-slot--end">
                <div className="cms-page__events-list-toolbar-selects">
                  <Select
                    value={listYear}
                    onChange={(year) =>
                      handleListMonthPartChange(year, listMonthIndex)
                    }
                    options={yearOptions}
                    density="compact"
                    fullWidth={false}
                  />
                  <Select
                    value={listMonthIndex}
                    onChange={(month) =>
                      handleListMonthPartChange(listYear, month)
                    }
                    options={MONTH_SELECT_LABELS}
                    density="compact"
                    fullWidth={false}
                  />
                </div>
              </div>
            </div>
            {isListLoading ? (
              <TableSkeleton columnCount={columns.length} actionCount={3} />
            ) : (
              <Table
                columns={columns}
                data={listEvents}
                getRowKey={(row) => row.id}
                rowActions={{
                  getWhatsAppHref: (row) =>
                    row.contact
                      ? generateWhatsAppLink(
                          row.contact.whatsapp,
                          buildManagerBookingContactWhatsAppMessage(
                            space?.name ?? "la sala",
                            row.contact?.name ?? "el cliente",
                            new Date(row.startsAt),
                          ),
                        )
                      : null,
                  getWhatsAppAriaLabel: (row) =>
                    row.contact
                      ? `Contactar a ${row.contact.name} por WhatsApp`
                      : "Contactar por WhatsApp",
                  onEdit: (row) => navigate(`${eventsPath}/${row.id}/editar`),
                  onDelete: (row) => void handleDelete(row),
                }}
                deleteConfirmation={{
                  title: "Eliminando evento",
                  description: (row) => `¿Eliminar "${row.title}"?`,
                }}
              />
            )}
          </>
        )}
      </div>

      {confirmCopy && (
        <ConfirmDialog
          open={pendingAction !== null}
          onOpenChange={(open) => {
            if (!open) {
              setPendingAction(null);
              setFocusedTarget(null);
            }
          }}
          title={confirmCopy.title}
          description={confirmCopy.description}
          confirmLabel={confirmCopy.confirmLabel}
          confirmVariant="primary"
          onConfirm={handleConfirmAction}
          confirmActionTrackSufix="bo_calendar_action_confirm"
          cancelActionTrackSufix="bo_calendar_action_cancel"
        />
      )}

      <RecurrenceScopeDialog
        open={scopeDialogOpen}
        onOpenChange={(open) => {
          setScopeDialogOpen(open);
          if (!open) setDeleteTarget(null);
        }}
        action="delete"
        onConfirm={handleScopeDelete}
      />
    </BackofficeTopBarContainer>
  );
}
